import React, { useCallback, useEffect, useRef, useState, CSSProperties } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { ITabs } from '../../types/customizer';
import './index.scss';

import { twMerge } from 'tailwind-merge';
import { FONT_MAP, getSelectedInfo } from '../../utils/commonRingUtils';
import If from '../If';
import { isRightSidebar } from '../../utils/basic';
import { useAppSelector } from '../../redux/hooks';
import { selectProduct, selectStoreData } from '../../redux/slices/customizer/customizerSlice';

interface Props {
  pages: ITabs;
  selectedTab: string;
  handleActiveTab: Function;
  price: string;
  handleDone: () => void;
  pathPrefix?: string;
  favouritesCount?: number;
  showFavourites?: boolean;
  onFavouritesClick?: () => void;
}

const formatShortDate = (date: string | undefined) => {
  if (!date) return '';
  return moment(date).format('MMM. D') + '*';
};

const DesktopSidebar = ({
  pages,
  selectedTab,
  handleActiveTab,
  price,
  handleDone,
  pathPrefix,
  favouritesCount = 0,
  showFavourites = false,
  onFavouritesClick
}: Props) => {
  const product = useAppSelector(selectProduct);
  const storeData = useAppSelector(selectStoreData);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [horizontalStyle, setHorizontalStyle] = useState<CSSProperties>({});
  const [verticalStyle, setVerticalStyle] = useState<CSSProperties>({});
  const isHelzberg = pathPrefix === 'helzberg';
  const [isTouchscreen, setIsTouchscreen] = useState(false);

  useEffect(() => {
    const isWindows = navigator.userAgent.includes('Windows');
    const isDesktopTouch = window.matchMedia('(pointer: coarse) and (min-width: 1024px)').matches;
    setIsTouchscreen(isWindows && isDesktopTouch);
  }, []);

  const checkScrollable = useCallback(() => {
    const el = sidebarRef.current;
    if (el) {
      setIsScrollable(el.scrollHeight > el.clientHeight);
    }
  }, []);

  useEffect(() => {
    checkScrollable();
    const el = sidebarRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(checkScrollable);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [checkScrollable]);

  useEffect(() => {
    checkScrollable();
  }, [pages, checkScrollable]);

  useEffect(() => {
    setHorizontalStyle(getHorizontalStyle());
    setVerticalStyle(getVerticalStyle());
  }, [selectedTab, pages]);

  const getHorizontalStyle = (): CSSProperties => {
    const tabContentRect = document.getElementById('main-content')?.getBoundingClientRect();
    const sidebarRect = document.getElementById(selectedTab)?.getBoundingClientRect();
    if (tabContentRect && sidebarRect) {
      let tabcontentTop = tabContentRect.top;
      const sidebarTopX = sidebarRect.top;
      const sidebarBottomX = sidebarRect.top + sidebarRect.height;

      const selectedPage = pages.find((item) => item.eventKey === selectedTab);
      const hasItems = selectedPage?.items?.length || 0;

      tabcontentTop = tabcontentTop + (hasItems ? 47 : 2);

      if (tabcontentTop - sidebarTopX > 20 && tabcontentTop - sidebarBottomX > 20) {
        return {
          bottom: '0px',
          top: 'unset'
        };
      } else if (sidebarTopX - tabcontentTop > 20 && sidebarBottomX - tabcontentTop > 20) {
        return {
          top: '0px',
          bottom: 'unset'
        };
      } else if (tabcontentTop - sidebarTopX > 20) {
        return {
          top: '0px',
          bottom: 'unset'
        };
      } else {
        return {
          bottom: '0px',
          top: 'unset'
        };
      }
    }
    return {};
  };

  const getVerticalStyle = (): CSSProperties => {
    const tabContentRect = document.getElementById('main-content')?.getBoundingClientRect();
    const sidebarRect = document.getElementById(selectedTab)?.getBoundingClientRect();
    if (tabContentRect && sidebarRect) {
      let tabcontentTop = tabContentRect.top;
      const sidebarTopX = sidebarRect.top;
      const sidebarBottomX = sidebarRect.top + sidebarRect.height;

      const selectedPage = pages.find((item) => item.eventKey === selectedTab);
      const hasItems = selectedPage?.items?.length || 0;

      tabcontentTop = tabcontentTop + (hasItems ? 38 : 2);

      let height = 0;
      if (tabcontentTop - sidebarTopX > 5 && tabcontentTop - sidebarBottomX > 5) {
        height = tabcontentTop - sidebarBottomX;
        return {
          height: height + 'px',
          top: '100%',
          bottom: 'unset'
        };
      } else if (sidebarTopX - tabcontentTop > 5 && sidebarBottomX - tabcontentTop > 5) {
        height = sidebarTopX - tabcontentTop;
        return {
          height: height + 'px',
          bottom: '100%',
          top: 'unset'
        };
      } else if (tabcontentTop - sidebarTopX > 5) {
        height = tabcontentTop - sidebarTopX;
        return {
          height: height + 'px',
          top: '0px',
          bottom: 'unset'
        };
      } else {
        height = sidebarBottomX - tabcontentTop;
        return {
          height: height + 'px',
          top: 'unset',
          bottom: '0px'
        };
      }
    }
    return {};
  };

  const getUrl = (swatch_url: string) => {
    return swatch_url.indexOf('http') !== -1 ? swatch_url : `${process.env.REACT_APP_ASSET_URL}${swatch_url}`;
  };

  const isRight = isRightSidebar(pathPrefix || '');
  // Hide for xs, sm, md devices
  return (
    <div
      className={clsx(
        'tw-hidden lg:tw-flex tw-flex-col tw-bg-primary-bg-color desktop-sidebar tw-w-full tw-h-full tw-overflow-hidden tw-grow-0 tw-shrink-0 lg:tw-basis-[280px] xl:tw-basis-[320px]',
        { left: !isRight, right: isRight }
      )}
    >
      {/* flex-basis and width both are same/ just used for advanced setup */}
      <div
        ref={sidebarRef}
        className="tw-w-full tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-overflow-x-hidden tw-pt-2 tw-flex tw-flex-col"
      >
        <div className="tw-flex tw-flex-col tw-w-full sidebar-tabs tw-mb-5">
          <p className="tw-mb-6 tw-mt-5 tw-text-sm tw-text-center">Powered by Lashbrook Builder</p>
          {pages.map((item) => {
            const selectedInfo = getSelectedInfo(item);
            const selectedText = selectedInfo?.text || '';

            return (
              <div
                id={item.eventKey}
                key={item.eventKey}
                className={clsx(
                  `tw-flex tw-flex-col tw-justify-center lg:tw-text-base tw-cursor-pointer tw-py-2 tw-border-gray-500 sidebar-tab tw-border-solid`,
                  {
                    active: selectedTab === item.eventKey,
                    'in-active': selectedTab !== item.eventKey
                  }
                )}
                onClick={handleActiveTab?.(item.eventKey, false)}
              >
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                  <div className="tw-flex tw-items-start tw-justify-center tw-flex-col tab-text">
                    {/* Sidebar title starts */}
                    <p className={'tw-relative tw-font-medium tw-whitespace-nowrap sidebar-title primary-font-family'}>
                      {typeof item.title === 'string' && item.title}
                      {typeof item.title === 'function' && item.title()}
                    </p>
                    {/* Sidebar title ends */}

                    {/* Sub content starts */}
                    <div>
                      {item?.disclaimer && (
                        <p className="tw-text-xs tw-mt-2 tw-text-primary-color tw-text-opacity-80">
                          {item?.disclaimer}
                        </p>
                      )}
                    </div>
                    {/* Sub content ends */}
                  </div>
                  <If condition={item.eventKey !== 'summary'}>
                    <div
                      className={clsx('tw-flex tw-flex-col tw-items-end', {
                        'tw-relative': selectedTab === item.eventKey
                      })}
                    >
                      <div className="tw-flex tw-items-center tw-gap-1.5">
                        {selectedText && (
                          <p className="tw-text-xs tw-text-right tw-text-primary-color tw-text-opacity-80">
                            {selectedText !== 'Soccer' && selectedInfo?.url?.includes('CER')
                              ? `Cerakote - ${selectedText}`
                              : selectedText}
                          </p>
                        )}
                        {selectedInfo?.url && !/^None$/i.test(selectedText) && (
                          <img
                            className="tw-rounded-full tw-h-7 tw-w-7 tw-shrink-0"
                            alt="img"
                            src={`${getUrl(selectedInfo?.url)}`}
                          />
                        )}
                      </div>
                      {item.eventKey === 'engraving' && item.selectedItem === 'text' && (
                        <p
                          className={twMerge(
                            'tw-text-xs tw-text-right tw-text-primary-color tw-text-opacity-80',
                            FONT_MAP[selectedText || '']
                          )}
                        >
                          {selectedInfo?.engravingText || '(none)'}
                        </p>
                      )}
                    </div>
                  </If>
                  <If condition={item.eventKey === 'summary' && !isHelzberg}>
                    <p className="tw-text-lg tw-text-center tw-text-brand-color">{price}</p>
                  </If>
                  <If condition={item.eventKey === 'summary'}>
                    <p
                      className={clsx('tw-text-xs tw-text-center tw-text-opacity-80', {
                        'tw-text-brand-color': selectedTab === item.eventKey,
                        'tw-text-primary-color': selectedTab !== item.eventKey
                      })}
                    >
                      {product?.magic_number}
                    </p>
                  </If>
                </div>
                <If condition={selectedTab === item.eventKey}>
                  {/* <div id="active-h-border" style={horizontalStyle}></div> */}
                  {!isScrollable && <div id="active-v-border" style={verticalStyle}></div>}
                </If>
              </div>
            );
          })}

          <If condition={favouritesCount > 0 && !!onFavouritesClick && pages.length > 0}>
            <div
              id="favourites-tab"
              className={clsx(
                `tw-flex tw-flex-col tw-justify-center lg:tw-text-base tw-cursor-pointer tw-py-2 tw-border-gray-500 sidebar-tab tw-border-solid tw-border-b`,
                {
                  active: showFavourites,
                  'in-active': !showFavourites
                }
              )}
              onClick={onFavouritesClick}
            >
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <div className="tw-flex tw-items-start tw-justify-center tw-flex-col tab-text">
                  <p className="tw-relative tw-font-medium tw-whitespace-nowrap sidebar-title primary-font-family">
                    <i className="fa fa-bookmark tw-mr-2" aria-hidden /> Compare Designs
                  </p>
                </div>
                <div className="tw-flex tw-flex-col tw-items-end">
                  <p className="tw-text-xs tw-text-right tw-text-primary-color tw-text-opacity-80">{favouritesCount}</p>
                </div>
              </div>
            </div>
          </If>

          <If condition={!!product?.shipping_options && !!storeData?.can_select_shipping_priority}>
            <div
              className="tw-hidden lg:tw-flex tw-flex-col tw-items-center tw-justify-center tw-mt-10 tw-mb-4 tw-text-center tw-cursor-pointer"
              onClick={(e) => {
                (window as any).__highlightShipDate = true;
                handleActiveTab?.('summary', false)?.(e as any);
                setTimeout(() => {
                  window.dispatchEvent(new Event('highlight-ship-date'));
                }, 100);
              }}
            >
              <div className="tw-text-sm">Choose Ship Date</div>
              {product?.shipping_options?.same_day && product?.shipping_options?.next_day ? (
                <div className="tw-text-base" style={{ color: '#000000' }}>
                  {formatShortDate(product.shipping_options.same_day)}
                </div>
              ) : (
                <>
                  {product?.shipping_options?.same_day && (
                    <div className="tw-text-base" style={{ color: '#000000' }}>
                      Same Day - {formatShortDate(product.shipping_options.same_day)}
                    </div>
                  )}
                  {product?.shipping_options?.next_day && (
                    <div className="tw-text-base" style={{ color: '#000000' }}>
                      Next Day - {formatShortDate(product.shipping_options.next_day)}
                    </div>
                  )}
                </>
              )}
              {product?.shipping_options?.rush && (
                <div className="tw-text-base" style={{ color: '#000000' }}>
                  Rush - {formatShortDate(product.shipping_options.rush)}
                </div>
              )}
              {product?.shipping_options?.standard && (
                <div className="tw-text-base" style={{ color: '#000000' }}>
                  Standard - {formatShortDate(product.shipping_options.standard)}
                </div>
              )}
            </div>
          </If>
        </div>

        <div
          className={clsx(
            'tw-sticky tw-bottom-0 tw-bg-primary-bg-color tw-z-10 tw-mt-auto tw-border-solid tw-border-primary-border-color',
            isScrollable ? 'tw-border-t-2 tw-shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]' : 'tw-border-t-0'
          )}
        >
          <div className="tw-flex tw-items-center tw-justify-center tw-w-full tw-pt-3 tw-pb-3 tw-bg-primary-bg-color">
            <button
              className="tw-bg-brand-color hover:tw-bg-brand-color tw-text-tertiary-color hover:tw-text-tertiary-color tw-w-full tw-max-w-[260px] tw-px-9 tw-py-2 tw-rounded-tertiary tw-font-medium tw-hidden lg:tw-block"
              onClick={handleDone}
            >
              {isHelzberg && pages.length && price ? `Done - ${price}` : 'Done'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopSidebar;
